import React, { useEffect, useMemo, useRef, useState } from "react";

export type TrackEvent = {
  title: string;
  dateLabel?: string;     // e.g. "Tuesday, 13 Jan"
  isActive?: boolean;     // first/current step
};

export type TrackData = {
  estimatedDeliveryTitle?: string; // e.g. "Thursday, 15 Jan"
  estimatedDeliverySub?: string;   // e.g. "TUESDAY, 13 JAN"
  events: TrackEvent[];
};

type Props = {
  open: boolean;
  onClose: () => void;

  trackingNumber?: string;
  carrierName?: string;

  /**
   * If you already have tracking data, pass it here and no API call is needed.
   */
  initialData?: TrackData | null;

  /**
   * If provided, the modal will call this when opened to fetch dynamic tracking data.
   */
  loadTracking?: () => Promise<TrackData>;
};

const STATIC_FALLBACK = (carrierName?: string): TrackData => ({
  estimatedDeliveryTitle: "—",
  estimatedDeliverySub: "—",
  events: [
    {
      title: carrierName
        ? `Shipper created a label, ${carrierName} has not received the package yet.`
        : "Shipper created a label, carrier has not received the package yet.",
      dateLabel: "—",
      isActive: true,
    },
    { title: "Arrived at Facility", dateLabel: "—" },
    { title: "Departed from Facility", dateLabel: "—" },
  ],
});

export default function TrackShipmentModal({
  open,
  onClose,
  trackingNumber,
  carrierName,
  initialData = null,
  loadTracking,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TrackData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loadTrackingRef = useRef(loadTracking);
  loadTrackingRef.current = loadTracking;

  useEffect(() => {
    if (!open) return;

    setData(null);
    setError(null);
    const fn = loadTrackingRef.current;

    if (!fn) {
      setData(initialData ?? STATIC_FALLBACK(carrierName));
      return;
    }

    let cancelled = false;
    setLoading(true);

    const run = async () => {
      try {
        const res = await fn();
        if (!cancelled) {
          setData(res);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : "Failed to load tracking.";
          setError(msg);
          setData({
            estimatedDeliveryTitle: "—",
            estimatedDeliverySub: "—",
            events: [{ title: `Error: ${msg}`, isActive: true }],
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [open, initialData, carrierName]);

  const safeData = useMemo<TrackData>(() => {
    if (data) return data;
    if (!loadTracking && initialData) return initialData;
    if (!loadTracking) return STATIC_FALLBACK(carrierName);
    return {
      estimatedDeliveryTitle: "—",
      estimatedDeliverySub: "—",
      events: loading
        ? [{ title: "Loading…", dateLabel: "", isActive: true }]
        : error
          ? [{ title: `Error: ${error}`, isActive: true }]
          : STATIC_FALLBACK(carrierName).events.map((e, i) => ({ ...e, isActive: i === 0 })),
    };
  }, [data, loadTracking, initialData, carrierName, loading, error]);

  if (!open) return null;

  return (
    <>
      <div className="tsm-backdrop" onClick={onClose} />
      <div className="tsm-modal" role="dialog" aria-modal="true">
        <div className="tsm-card">
          <div className="tsm-header">
            <div className="tsm-header-text">
              <div className="tsm-title">Track Shipment</div>
              {(trackingNumber || carrierName) && (
                <div className="tsm-subtitle">
                  {[trackingNumber, carrierName].filter(Boolean).join(" • ")}
                </div>
              )}
            </div>
            <button className="tsm-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>

          <div className="tsm-body">
            <div className="tsm-section-title">Shipping Activity</div>

            <div className="tsm-estimate">
              <div className="tsm-estimate-label">Estimated Delivery Date</div>
              <div className="tsm-estimate-date">{safeData.estimatedDeliveryTitle || "—"}</div>
              <div className="tsm-estimate-sub">{safeData.estimatedDeliverySub || "—"}</div>
            </div>

            <div className="tsm-timeline">
              {loading ? (
                <div className="text-muted py-3">Loading tracking details…</div>
              ) : (
                safeData.events.map((ev, idx) => {
                  const active = ev.isActive ?? idx === 0;
                  return (
                    <div className="tsm-row" key={`${idx}-${ev.title}`}>
                      <div className="tsm-left">
                        <div className={`tsm-dot ${active ? "active" : ""}`} />
                        {idx !== safeData.events.length - 1 && <div className="tsm-line" />}
                      </div>

                      <div className="tsm-right">
                        <div className={`tsm-ev-title ${active ? "active" : ""}`}>{ev.title}</div>
                        {ev.dateLabel ? <div className="tsm-ev-date">• {ev.dateLabel}</div> : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="tsm-footer-note">Times are shown in the local time zone.</div>
          </div>
        </div>
      </div>

      <style>{`
        .tsm-backdrop{
          position:fixed; inset:0;
          background: rgba(0,0,0,.35);
          z-index: 1050;
        }
        .tsm-modal{
          position:fixed; inset:0;
          display:flex; align-items:center; justify-content:center;
          z-index: 1060;
          padding: 16px;
        }
        .tsm-card{
          width: 720px;
          max-width: 100%;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background:#fff;
          border-radius: 14px;
          box-shadow: 0 18px 60px rgba(0,0,0,.18);
          border: 1px solid #e6eaf0;
        }
        .tsm-header{
          flex-shrink: 0;
          display:flex; align-items:center; justify-content:center;
          position: relative;
          padding: 18px 18px 10px 18px;
        }
        .tsm-header-text{ text-align: center; }
        .tsm-title{
          font-weight: 700;
          font-size: 18px;
          color: #111827;
        }
        .tsm-subtitle{
          font-size: 13px;
          color: #6b7280;
          margin-top: 4px;
        }
        .tsm-close{
          position:absolute; right: 14px; top: 14px;
          width: 30px; height: 30px;
          border-radius: 6px;
          border: 2px solid #d1d5db;
          background: #fff;
          color:#111827;
          line-height: 1;
          display:flex; align-items:center; justify-content:center;
          cursor:pointer;
        }
        .tsm-body{
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
          padding: 10px 22px 18px 22px;
        }
        .tsm-section-title{
          flex-shrink: 0;
          font-weight: 700;
          font-size: 14px;
          color:#111827;
          margin-bottom: 10px;
        }
        .tsm-estimate{
          flex-shrink: 0;
          background: #eff6ff;
          border: 1px solid #dbeafe;
          border-radius: 10px;
          padding: 14px 14px;
          margin-bottom: 14px;
        }
        .tsm-estimate-label{
          color:#6b7280;
          font-size: 12px;
          margin-bottom: 4px;
        }
        .tsm-estimate-date{
          color:#1d4ed8;
          font-size: 16px;
          font-weight: 800;
          margin-bottom: 2px;
        }
        .tsm-estimate-sub{
          color:#6b7280;
          font-size: 12px;
          text-transform: uppercase;
        }
        .tsm-timeline{
          flex: 1;
          min-height: 0;
          margin-top: 6px;
          overflow-y: auto;
          overscroll-behavior: contain;
        }
        .tsm-row{
          display:flex;
          gap: 12px;
          padding: 12px 0;
        }
        .tsm-left{
          width: 18px;
          position: relative;
          display:flex;
          justify-content:center;
        }
        .tsm-dot{
          width: 12px; height: 12px;
          border-radius: 999px;
          background:#d1d5db;
          margin-top: 3px;
        }
        .tsm-dot.active{
          background:#2563eb;
          box-shadow: 0 0 0 4px rgba(37,99,235,.18);
        }
        .tsm-line{
          position:absolute;
          top: 18px;
          bottom: -6px;
          width: 2px;
          background:#e5e7eb;
        }
        .tsm-right{
          flex:1;
          padding-right: 6px;
        }
        .tsm-ev-title{
          font-size: 13px;
          color:#111827;
          font-weight: 600;
        }
        .tsm-ev-title.active{
          font-weight: 700;
        }
        .tsm-ev-date{
          margin-top: 6px;
          font-size: 12px;
          color:#6b7280;
        }
        .tsm-footer-note{
          flex-shrink: 0;
          margin-top: 14px;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
          text-align:center;
          color:#6b7280;
          font-size: 12px;
        }
      `}</style>
    </>
  );
}
